<script lang="ts">
	import { format } from 'svelte-i18n';
	import { querystring } from 'svelte-spa-router';

	import type { Device } from 'src/types/Device';
	import type { PVSetupTypes } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';

	import { devices, home_data } from 'src/stores';

	import SolarSetupSettingsModal from './SolarSetupSettingsModal.svelte';

	import {
		ButtonV2,
		Icon,
		IconButton,
		Modal,
		PlainTable,
		Stack,
		Text,
		Tooltip,
		toast,
	} from '@Shelly/shelly-design-system';

	import {
		getPVSetupDevices,
		getPVSetupTypes,
	} from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { delete_home, save_home } from 'src/helpers/home_helpers';
	import { MAIN_ENERGY_METER_KEY } from './shared/main-energy-meter-helpers';
	import {
		getActiveSolarSetupType,
		getMainEnergyMeterPromptCandidate,
		getPvModeMeterIds,
		isSolarSetupConfigured,
		planMainMeterApplianceTypes,
		planSolarSetupApplianceTypes,
		type PvConfigurations,
	} from './shared/solar-setup-helpers';
	import { applyApplianceTypeSyncChanges } from './shared/solar-setup-persist';

	let showModal = false;
	let showDeleteModal = false;
	let deleting = false;
	let showMainMeterPrompt = false;
	let pendingMainMeterDevice: Device | null = null;
	let savingMainMeter = false;
	let openedFromQuery = false;

	$: setupType = getActiveSolarSetupType($home_data);
	$: meterLists = getPvModeMeterIds($home_data, setupType);
	$: pvSetupDevices = getPVSetupDevices(setupType);
	$: isConfigured = isSolarSetupConfigured(meterLists);
	$: hasSavedMeters = meterLists.gridDevices.length > 0 || meterLists.solarDevices.length > 0;
	$: modeLabel = getPVSetupTypes().find((type) => type.title === setupType)?.translation || setupType;
	$: gridMeterNames = pvSetupDevices.gridDevices
		.map((device) => device.name || device.type)
		.filter(Boolean)
		.join(', ');
	$: solarMeterNames = pvSetupDevices.solarDevices
		.map((device) => device.name || device.type)
		.filter(Boolean)
		.join(', ');
	$: if (!openedFromQuery && new URLSearchParams($querystring).has('pvSetup')) {
		openedFromQuery = true;
		showModal = true;
	}

	const showError = (): void => {
		toast.open({
			type: 'danger',
			title: $format('error_global', { default: 'Something went wrong!' }),
		});
	};

	const handleOpenModal = (): void => {
		showModal = true;
	};

	const handleModalClose = (): void => {
		showModal = false;
	};

	const handleSetupSaved = (e: CustomEvent<{ gridDeviceIds: string[] }>): void => {
		showModal = false;
		toast.open({
			type: 'success',
			title: $format('toast_solar_setup_saved', { default: 'Solar setup saved' }),
			duration: 4000,
		});

		const gridDevices = e.detail.gridDeviceIds
			.map((id) => $devices[id] as Device | undefined)
			.filter((device): device is Device => Boolean(device));
		const candidate = getMainEnergyMeterPromptCandidate(
			gridDevices,
			($home_data?.[MAIN_ENERGY_METER_KEY] as string) || null,
		);
		if (!candidate) return;
		pendingMainMeterDevice = candidate;
		showMainMeterPrompt = true;
	};

	const handleOpenDeleteModal = (): void => {
		showDeleteModal = true;
	};

	const handleCloseDeleteModal = (): void => {
		if (deleting) return;
		showDeleteModal = false;
	};

	const handleDeleteConfirm = async (): Promise<void> => {
		if (deleting) return;
		deleting = true;

		const previous = getPVSetupDevices(setupType);
		const configurations = { ...(($home_data?.pv_configurations || {}) as PvConfigurations) };
		delete configurations[setupType];

		const remainingModes = Object.keys(configurations) as PVSetupTypes[];
		if (remainingModes.length === 0) {
			const configRes = await delete_home('pv_configurations');
			if (!configRes?.isok) {
				deleting = false;
				showError();
				return;
			}
			const typeRes = await delete_home('net_metering_type');
			if (!typeRes?.isok) {
				deleting = false;
				showError();
				return;
			}
		} else {
			const configRes = await save_home('pv_configurations', configurations);
			if (!configRes?.isok) {
				deleting = false;
				showError();
				return;
			}
			const firstRemainingMode = remainingModes[0];
			if ($home_data?.net_metering_type === setupType && firstRemainingMode) {
				const typeRes = await save_home('net_metering_type', firstRemainingMode);
				if (!typeRes?.isok) {
					deleting = false;
					showError();
					return;
				}
			}
		}

		await applyApplianceTypeSyncChanges(
			planSolarSetupApplianceTypes({
				previousGridIds: previous.gridDevices.map((device) => device.id),
				previousSolarIds: previous.solarDevices.map((device) => device.id),
				nextGridIds: [],
				nextSolarIds: [],
				mainEnergyMeterId: ($home_data?.[MAIN_ENERGY_METER_KEY] as string) || null,
			}),
		);

		deleting = false;
		showDeleteModal = false;
		toast.open({
			type: 'success',
			title: $format('toast_solar_setup_deleted', { default: 'Solar setup removed' }),
			duration: 4000,
		});
	};

	const handleCloseMainMeterPrompt = (): void => {
		if (savingMainMeter) return;
		showMainMeterPrompt = false;
		pendingMainMeterDevice = null;
	};

	const handleConfirmMainMeter = async (): Promise<void> => {
		if (!pendingMainMeterDevice || savingMainMeter) return;
		savingMainMeter = true;
		const previousMainId = ($home_data?.[MAIN_ENERGY_METER_KEY] as string) || null;
		const response = await save_home(MAIN_ENERGY_METER_KEY, pendingMainMeterDevice.id);
		if (!response?.isok) {
			savingMainMeter = false;
			showError();
			return;
		}

		await applyApplianceTypeSyncChanges(
			planMainMeterApplianceTypes({
				previousMainId,
				nextMainId: pendingMainMeterDevice.id,
				solarGridIds: getPvModeMeterIds($home_data, setupType).gridDevices,
			}),
		);

		savingMainMeter = false;
		showMainMeterPrompt = false;
		pendingMainMeterDevice = null;
		toast.open({
			type: 'success',
			title: $format('label_energy_meter_connected', { default: 'Main energy meter saved' }),
			duration: 4000,
		});
	};
</script>

<Stack
	class="align-items-center"
	direction="column"
	directionMobile="row"
	align="between"
	alignMobile="none"
	gap="m"
>
	<div>
		<Text styleType="body-medium" class="mb-xs">
			{$format('label_solar_setup', { default: 'Solar setup' })}
		</Text>
		<Text styleType="body-small" class="text-color-muted">
			{$format('settings_solar_setup_description', {
				default:
					'Choose how solar is wired in this home and designate the grid and generation meters.',
			})}
		</Text>
		{#if isConfigured || hasSavedMeters}
			<PlainTable type="compact" class="mt-s width-full">
				<tbody>
					<tr>
						<th>
							{$format('solar_setup_step_mode', { default: 'Mode' })}
						</th>
						<td>
							<span class="d-inline-flex align-items-center justify-content-end gap-xs">
								{#if isConfigured}
									<Icon icon="fas fa-circle-check" size="s" class="text-success" />
								{/if}
								{modeLabel}
							</span>
						</td>
					</tr>
					<tr>
						<th>
							{$format('solar_setup_step_grid', { default: 'Grid meters' })}
						</th>
						<td>
							{gridMeterNames ||
								$format('solar_setup_summary_none', { default: 'None selected' })}
						</td>
					</tr>
					<tr>
						<th>
							{$format('solar_setup_step_solar', { default: 'Solar meters' })}
						</th>
						<td>
							{solarMeterNames ||
								$format('solar_setup_summary_none', { default: 'None selected' })}
						</td>
					</tr>
				</tbody>
			</PlainTable>
		{/if}
	</div>

	<div class="width-md-auto width-sm-full">
		{#if isConfigured || hasSavedMeters}
			<div class="d-flex align-items-center justify-content-end gap-s">
				<Tooltip
					title={$format('button_edit_solar_setup', { default: 'Change solar setup' })}
					placement="top"
				>
					<IconButton
						variant="secondary"
						icon="fas fa-pen"
						aria-label={$format('button_edit_solar_setup', {
							default: 'Change solar setup',
						})}
						on:click={handleOpenModal}
					/>
				</Tooltip>
				<Tooltip
					title={$format('button_delete_solar_setup', { default: 'Delete solar setup' })}
					placement="top"
				>
					<IconButton
						variant="danger-secondary"
						shape="circle"
						icon="fas fa-trash"
						aria-label={$format('button_delete_solar_setup', {
							default: 'Delete solar setup',
						})}
						on:click={handleOpenDeleteModal}
					/>
				</Tooltip>
			</div>
		{:else}
			<ButtonV2
				variant="secondary"
				size="m"
				class="width-full width-md-auto"
				aria-label={$format('button_add_solar_setup', { default: 'Add solar setup' })}
				on:click={handleOpenModal}
			>
				{$format('button_add', { default: 'Add' })}
			</ButtonV2>
		{/if}
	</div>
</Stack>

<SolarSetupSettingsModal show={showModal} on:close={handleModalClose} on:save={handleSetupSaved} />

<Modal
	showModal={showDeleteModal}
	title={$format('confirm_delete_solar_setup_title', { default: 'Delete solar setup?' })}
	backdropClose={!deleting}
	on:close={handleCloseDeleteModal}
>
	<svelte:fragment slot="content">
		<Text styleType="body">
			{$format('confirm_delete_solar_setup_message', {
				default: 'This home will no longer have solar meters designated for energy dashboards.',
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			class="min-width-md-m min-width-sm-auto"
			disabled={deleting}
			on:click={handleCloseDeleteModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			aria-label={$format('button_delete', { default: 'Delete' })}
			class="min-width-md-m min-width-sm-auto"
			loading={deleting}
			disabled={deleting}
			on:click={handleDeleteConfirm}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showMainMeterPrompt}
	title={$format('confirm_solar_grid_as_main_meter_title', {
		default: 'Set as main energy meter?',
	})}
	backdropClose={!savingMainMeter}
	on:close={handleCloseMainMeterPrompt}
>
	<svelte:fragment slot="content">
		<Text styleType="body">
			{$format('confirm_solar_grid_as_main_meter_message', {
				default:
					"{name} is measuring grid consumption for solar setup, but it is not this home's main energy meter. Set it as the main energy meter?",
				values: { name: pendingMainMeterDevice?.name || pendingMainMeterDevice?.type || '' },
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_not_now', { default: 'Not now' })}
			disabled={savingMainMeter}
			on:click={handleCloseMainMeterPrompt}
		>
			{$format('button_not_now', { default: 'Not now' })}
		</ButtonV2>
		<ButtonV2
			aria-label={$format('button_set_main_energy_meter', { default: 'Set as main energy meter' })}
			loading={savingMainMeter}
			disabled={savingMainMeter}
			on:click={handleConfirmMainMeter}
		>
			{$format('button_set_main_energy_meter', { default: 'Set as main energy meter' })}
		</ButtonV2>
	</div>
</Modal>
